import { ChirpIcon } from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import { projectsPlugin } from '@spotify/plugin-confidence-projects';
import {
  ExternalUrlArtifactContent,
  ExternalUrlArtifactListItem,
  createArtifactExtension,
  getCodingSessionRef,
  getCodingSessionUrl,
} from '@spotify/plugin-confidence-projects-react';

const HONK_PROVIDER = 'honk';

const getLegacyChirpSessionId = (uri: string): string | undefined => {
  try {
    const { hostname, pathname } = new URL(uri);
    const match = pathname.match(/^\/chirp\/sessions\/([^/]+)$/);
    if (hostname === 'backstage.spotify.net' && match) {
      return match[1];
    }
  } catch {
    return undefined;
  }

  return undefined;
};

const getChirpSessionId = (
  artifact: AttachmentFragment,
): string | undefined => {
  const session = getCodingSessionRef(artifact.uri);
  if (session?.provider === HONK_PROVIDER) {
    return session.sessionId;
  }
  return getLegacyChirpSessionId(artifact.uri);
};

const getChirpHref = (artifact: AttachmentFragment): string | undefined => {
  if (getLegacyChirpSessionId(artifact.uri)) {
    return artifact.uri;
  }
  const session = getCodingSessionRef(artifact.uri);
  return session?.provider === HONK_PROVIDER
    ? getCodingSessionUrl(artifact)
    : undefined;
};

const isChirpCodingSession = (artifact: AttachmentFragment): boolean =>
  getChirpSessionId(artifact) !== undefined;

export const ChirpCodingSessionArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'chirp-coding-session',
    predicate: isChirpCodingSession,
    icon: () => ({
      Icon: ChirpIcon,
      color: 'var(--essential-warning)',
    }),
    label: 'Chirp coding session',
    category: 'Coding sessions',
    render: artifact => (
      <ExternalUrlArtifactContent
        artifact={artifact}
        href={getChirpHref(artifact)}
       data-track-source-anchor="7438caf774;chirp-coding-session-artifact-extension-external"/>
    ),
    renderListItem: (artifact, { onDelete, onRename, icon }) => {
      const href = getChirpHref(artifact);
      return (
        <ExternalUrlArtifactListItem
          artifact={artifact}
          icon={icon}
          subtitleLabel="Chirp coding session"
          openLabel="Open in Chirp"
          href={href}
          onDelete={onDelete}
          onRename={onRename}
         data-track-source-anchor="f7419de822;chirp-coding-session-artifact-extension-extern-2"/>
      );
    },
  }),
);
